import { Table, TableBody } from "@mui/material";
import Stack from "@mui/material/Stack";
import React, { FC } from "react";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import TranslationInput from "shared/components/Translations/TranslationInput";
import { languages, LocaleCode } from "shared/types/languages";
import { ManagedTranslations } from "../../../../types";
import { useGameDetails } from "../../context";
import { useMetadata } from "../../stores/useMetadata";
import {
  StyledTableCell,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
} from "./styled";

const Translations: FC = () => {
  const { translations, setTranslations, gameDetails } = useGameDetails();
  const {
    gameDescription,
    setGameDescription,
    gameShortDescription,
    setGameShortDescription,
    gameOptimisedDescription,
    setGameOptimisedDescription,
    rtpExact,
    setRtpExact,
    paylines,
    setPaylines,
    reels,
    setReels,
    rows,
    setRows,
    releaseDate,
    setReleaseDate,
    minStake,
    setMinStake,
    maxStake,
    setMaxStake,
    maxWin,
    setMaxWin,
  } = useMetadata();

  const getTranslationByLanguage = (
    translationKey: string,
    languageId: LocaleCode,
    defaultValue: string,
  ) =>
    languageId === LocaleCode.en
      ? defaultValue
      : translations?.[languageId]?.[translationKey] || "";

  const changeTranslationByLanguage = (
    translationKey: string,
    languageId: LocaleCode,
    value: string,
    setter: (value: any) => void,
  ) => {
    if (languageId === LocaleCode.en) {
      setter(value);
    } else {
      setTranslations({
        ...translations,
        [languageId]: {
          ...translations?.[languageId],
          [translationKey]: value,
        },
      });
    }
  };

  const renderArrayTranslationRows = (
    label: string,
    defaultValues: string[],
    setter: (values: string[]) => void,
  ) =>
    defaultValues.map((defaultValue, i) => (
      <StyledTableRow key={`${label}-${i}`}>
        <StyledTableCell style={{ padding: "12px", paddingTop: "16px" }}>
          {`${label} ${i + 1}`}
        </StyledTableCell>
        {languages.map((language) => (
          <StyledTableCell key={language.id}>
            <TranslationInput
              defaultValue={getTranslationByLanguage(
                `${label} ${i + 1}`,
                language.id,
                defaultValue,
              )}
              onChange={({ target: { value } }) => {
                const updatedValues = [...defaultValues];
                updatedValues[i] = value;
                if (language.id === LocaleCode.en) {
                  setter(updatedValues);
                } else {
                  setTranslations({
                    ...translations,
                    [language.id]: {
                      ...translations?.[language.id],
                      [`${label} ${i + 1}`]: value,
                    },
                  });
                }
              }}
            />
          </StyledTableCell>
        ))}
      </StyledTableRow>
    ));

  const renderSingleTranslationRow = (
    label: string,
    defaultValue: string,
    setter: (value: any) => void,
  ) => (
    <StyledTableRow>
      <StyledTableCell style={{ padding: "12px", paddingTop: "16px" }}>
        {label}
      </StyledTableCell>
      {languages.map((language) => (
        <StyledTableCell key={language.id}>
          <TranslationInput
            defaultValue={getTranslationByLanguage(
              label,
              language.id,
              defaultValue,
            )}
            onChange={({ target: { value } }) =>
              changeTranslationByLanguage(label, language.id, value, setter)
            }
          />
        </StyledTableCell>
      ))}
    </StyledTableRow>
  );

  return (
    <Stack spacing="1rem">
      <ContentSection>
        <Table>
          <StyledTableHead>
            <StyledTableRow>
              <StyledTableHeadCell>Content Type</StyledTableHeadCell>
              {languages.map((language) => (
                <StyledTableHeadCell key={language.id}>
                  {language.name}
                </StyledTableHeadCell>
              ))}
            </StyledTableRow>
          </StyledTableHead>
          <TableBody>
            {renderSingleTranslationRow(
              ManagedTranslations.GameName,
              gameDetails?.displayName || "",
              () => {},
            )}

            {gameDescription &&
              renderSingleTranslationRow(
                ManagedTranslations.GameDescription,
                gameDescription,
                setGameDescription,
              )}

            {gameOptimisedDescription &&
              renderSingleTranslationRow(
                ManagedTranslations.GameOptimisedDescription,
                gameOptimisedDescription,
                setGameOptimisedDescription,
              )}
            {gameShortDescription &&
              renderSingleTranslationRow(
                ManagedTranslations.GameShortDescription,
                gameShortDescription,
                setGameShortDescription,
              )}

            {rtpExact.length
              ? renderArrayTranslationRows(
                  ManagedTranslations.RTPExact,
                  rtpExact,
                  setRtpExact,
                )
              : null}

            {paylines &&
              renderSingleTranslationRow(
                ManagedTranslations.Paylines,
                paylines?.toString() || "",
                setPaylines,
              )}

            {reels &&
              renderSingleTranslationRow(
                ManagedTranslations.Reels,
                reels?.toString() || "",
                setReels,
              )}

            {rows &&
              renderSingleTranslationRow(
                ManagedTranslations.Rows,
                rows,
                setRows,
              )}

            {releaseDate &&
              renderSingleTranslationRow(
                ManagedTranslations.ReleaseDate,
                releaseDate,
                setReleaseDate,
              )}

            {minStake &&
              renderSingleTranslationRow(
                ManagedTranslations.MinStake,
                minStake,
                setMinStake,
              )}

            {maxStake &&
              renderSingleTranslationRow(
                ManagedTranslations.MaxStake,
                maxStake,
                setMaxStake,
              )}

            {maxWin &&
              renderSingleTranslationRow(
                ManagedTranslations.MaxWin,
                maxWin,
                setMaxWin,
              )}
          </TableBody>
        </Table>
      </ContentSection>
    </Stack>
  );
};

export default Translations;
